import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BadgeCheck, BellRing, CalendarCheck, Phone, Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection, SettingsToggle } from '@/components/settings/SettingsPage';
import { bookingSiteService, BookingSiteSettings as Settings } from '@/services/booking-site.service';
import { templatesService, Template } from '@/services/templates.service';

const HEADER = {
  eyebrow: 'Channels',
  title: 'Booking site',
  description: 'A public booking site for your reception tablet or front desk. Every booking made there lands in this workspace.',
};

// Only the editable fields — the API refuses unknown keys like companyUid / _id / timestamps.
const payloadOf = (x: Settings) => ({
  isEnabled: x.isEnabled, kioskPhones: x.kioskPhones, sendConfirmation: x.sendConfirmation, sendReminders: x.sendReminders,
  reminderHoursBefore: x.reminderHoursBefore, otpTemplateUid: x.otpTemplateUid, confirmationTemplateUid: x.confirmationTemplateUid,
  reminderTemplateUid: x.reminderTemplateUid,
});

// An unset template can come back as null or be undefined locally; both mean "none".
const fingerprint = (x: Settings) => JSON.stringify(payloadOf(x), (_key, v) => (v === undefined ? null : v));

function TemplateSelect({ value, onChange, options, none }: {
  value?: string; onChange: (v: string) => void; options: Template[]; none: string;
}) {
  return (
    <Select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">{none}</option>
      {options.map((t) => <option key={t.templateUid} value={t.templateUid}>{t.name} ({t.language})</option>)}
    </Select>
  );
}

/**
 * Settings → Booking site. The kiosk (public booking website) logs in with one
 * of these phone numbers + a WhatsApp OTP; every booking made in that session
 * lands on this company.
 */
export default function BookingSiteSettings() {
  const [s, setS] = useState<Settings | null>(null);
  const [saved, setSaved] = useState<Settings | null>(null);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [saving, setSaving] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  // Raw text of the reminder-hours box, so "24," can be typed before the next number.
  const [hoursText, setHoursText] = useState<string | null>(null);

  const load = () => {
    setLoadFailed(false);
    bookingSiteService.getSettings().then((r) => { setS(r); setSaved(r); }).catch(() => setLoadFailed(true));
  };
  useEffect(() => {
    load();
    templatesService.list({ status: 'APPROVED', limit: 100 })
      .then((r) => setTemplates(r.items ?? []))
      .catch(() => setTemplates([]));
  }, []);

  if (!s) {
    return (
      <SettingsPage {...HEADER}>
        <div className="py-7">
          {loadFailed ? (
            <LoadErrorState what="your booking site settings" onRetry={load} />
          ) : (
            <div className="space-y-4"><div className="skeleton h-28 rounded-2xl" /><div className="skeleton h-56 rounded-2xl" /><div className="skeleton h-64 rounded-2xl" /></div>
          )}
        </div>
      </SettingsPage>
    );
  }

  const patch = (p: Partial<Settings>) => setS((cur) => (cur ? { ...cur, ...p } : cur));
  const dirty = !!saved && fingerprint(s) !== fingerprint(saved);

  const addPhone = () => {
    const digits = newPhone.replace(/\D+/g, '');
    if (digits.length < 7) { toast.error('Enter the full number with country code, e.g. 9715xxxxxxx'); return; }
    if (s.kioskPhones.includes(digits)) { toast.error('Already in the list'); return; }
    patch({ kioskPhones: [...s.kioskPhones, digits] });
    setNewPhone('');
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await bookingSiteService.updateSettings(payloadOf(s));
      setS(updated);
      setSaved(updated);
      setHoursText(null);
      toast.success('Booking site settings saved');
    } catch {
      /* axios interceptor toasts */
    } finally { setSaving(false); }
  };

  const discard = () => {
    if (saved) setS(saved);
    setHoursText(null);
  };

  const auth = templates.filter((t) => t.category === 'authentication');
  const utility = templates.filter((t) => t.category === 'utility');

  return (
    <SettingsPage
      {...HEADER}
      actions={dirty && <Button iconLeft={<Save size={14} />} loading={saving} onClick={save}>Save changes</Button>}
    >
      <SettingsSection
        title="Kiosk login"
        description="Your front desk logs in to the booking site with one of these WhatsApp numbers. A one-time code is sent to the number on WhatsApp."
      >
        <SettingsCard padded={false}>
          <SettingsToggle
            icon={<CalendarCheck size={16} />}
            label="Enable kiosk login"
            description="When off, the numbers below can’t log in to the booking site."
            checked={s.isEnabled}
            onChange={(next) => patch({ isEnabled: next })}
          />

          <div className="border-t border-border">
            {s.kioskPhones.length === 0 ? (
              <div className="flex flex-col items-center gap-3 px-6 py-10 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><Phone size={20} /></div>
                <div className="text-sm font-medium text-text-primary">No login numbers yet</div>
                <p className="max-w-sm text-xs text-text-muted">Add the WhatsApp number your front desk will use to log in.</p>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {s.kioskPhones.map((p) => (
                  <li key={p} className="group flex items-center gap-3 px-5 py-3 transition-colors hover:bg-surface-alt/60">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-alt text-text-secondary"><Phone size={15} /></span>
                    <span className="min-w-0 flex-1 truncate font-mono text-sm text-text-primary">+{p}</span>
                    <button
                      type="button"
                      onClick={() => patch({ kioskPhones: s.kioskPhones.filter((x) => x !== p) })}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100"
                      aria-label={`Remove +${p}`}
                      title="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-border px-5 py-4">
            <label htmlFor="kiosk-phone" className="mb-1.5 block text-[13px] font-medium text-text-secondary">Add a number</label>
            <div className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <Input
                  id="kiosk-phone"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="e.g. 971501234567 (with country code)"
                  onKeyDown={(e) => e.key === 'Enter' && addPhone()}
                />
              </div>
              <Button variant="secondary" iconLeft={<Plus size={14} />} onClick={addPhone}>Add</Button>
            </div>
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="Messages to attendees"
        description="WhatsApp messages sent to the person who booked."
      >
        <SettingsCard padded={false}>
          <div className="divide-y divide-border">
            <SettingsToggle
              icon={<BadgeCheck size={16} />}
              label="Send booking confirmation"
              description="A WhatsApp message as soon as someone books."
              checked={s.sendConfirmation}
              onChange={(next) => patch({ sendConfirmation: next })}
            />
            <SettingsToggle
              icon={<BellRing size={16} />}
              label="Send appointment reminders"
              description="A WhatsApp reminder before the appointment."
              checked={s.sendReminders}
              onChange={(next) => patch({ sendReminders: next })}
            />
          </div>
          {s.sendReminders && (
            <div className="border-t border-border px-5 pt-4">
              <FormField label="Remind (hours before)" hintBelow hint="Separate with commas, for example 24, 2. One WhatsApp reminder is sent for each number.">
                <Input
                  value={hoursText ?? s.reminderHoursBefore.join(', ')}
                  onChange={(e) => {
                    setHoursText(e.target.value);
                    patch({ reminderHoursBefore: e.target.value.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0) });
                  }}
                />
              </FormField>
            </div>
          )}
        </SettingsCard>
      </SettingsSection>

      <SettingsSection
        title="WhatsApp templates"
        description="Approved templates reach anyone. Plain text only reaches people who messaged you in the last 24 hours. For a live kiosk, choose all three."
      >
        <SettingsCard
          padded={false}
          footer={dirty ? (
            <>
              <span className="mr-auto inline-flex items-center gap-2 text-xs font-medium text-text-secondary">
                <span className="h-1.5 w-1.5 rounded-full bg-warning" /> Unsaved changes on this page
              </span>
              <Button variant="ghost" size="sm" iconLeft={<RotateCcw size={14} />} onClick={discard}>Discard</Button>
              <Button size="sm" iconLeft={<Save size={14} />} loading={saving} onClick={save}>Save changes</Button>
            </>
          ) : undefined}
        >
          <div className="px-5 pb-1 pt-5">
            <FormField label="Login code (authentication)" hintBelow hint="The message body uses {{1}} for the code. Needed for reliable code delivery.">
              <TemplateSelect
                value={s.otpTemplateUid}
                onChange={(v) => patch({ otpTemplateUid: v || undefined })}
                options={auth}
                none={auth.length ? 'Plain text (24-hour window only)' : 'No approved authentication templates'}
              />
            </FormField>
            <FormField label="Booking confirmation (utility)" hintBelow hint="Fills in: service, company, date, time.">
              <TemplateSelect
                value={s.confirmationTemplateUid}
                onChange={(v) => patch({ confirmationTemplateUid: v || undefined })}
                options={utility}
                none={utility.length ? 'Plain text (24-hour window only)' : 'No approved utility templates'}
              />
            </FormField>
            <FormField label="Reminder (utility)" hintBelow hint="Fills in: company, service, date, time.">
              <TemplateSelect
                value={s.reminderTemplateUid}
                onChange={(v) => patch({ reminderTemplateUid: v || undefined })}
                options={utility}
                none={utility.length ? 'Plain text (24-hour window only)' : 'No approved utility templates'}
              />
            </FormField>
          </div>
        </SettingsCard>
      </SettingsSection>
    </SettingsPage>
  );
}
